/**
 * Shared bits of the AI object designer (New object → "Describe it", and the
 * editor's AI dialog): limits mirrored from the server's objectDefinitionGenerate,
 * the error codes it fails with, and reference-image preparation.
 */

import { CombinedGraphQLErrors } from '@apollo/client/errors';

/** Mirrors the server's prompt limit. */
export const AI_MAX_PROMPT_LENGTH = 4000;
/** Mirrors the server's decoded-image limit. */
export const AI_MAX_IMAGE_BYTES = 4 * 1024 * 1024;
/** Longest image edge sent: larger images are downscaled (the model reads ~1.5k px anyway). */
export const AI_IMAGE_MAX_EDGE = 1568;

export const AI_IMAGE_TYPES: readonly string[] = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

/** The failures the UI explains; everything else is `OTHER`. */
export type AiErrorCode = 'AI_NOT_CONFIGURED' | 'AI_INVALID_SPEC' | 'AI_REFUSED' | 'AI_OFF_TOPIC' | 'RATE_LIMITED' | 'INVALID_IMAGE' | 'OTHER';

const KNOWN_CODES: readonly AiErrorCode[] = ['AI_NOT_CONFIGURED', 'AI_INVALID_SPEC', 'AI_REFUSED', 'AI_OFF_TOPIC', 'RATE_LIMITED', 'INVALID_IMAGE'];

/** The code of a failed objectDefinitionGenerate call. */
export function aiErrorCode(error: unknown): AiErrorCode {
  if (!CombinedGraphQLErrors.is(error)) return 'OTHER';
  const message = error.errors[0]?.message;
  return KNOWN_CODES.find((code) => code === message) ?? 'OTHER';
}

/** Whether a picked/pasted file can be used as a reference image. */
export function isAcceptedImageType(type: string): boolean {
  return AI_IMAGE_TYPES.includes(type);
}

/** Decoded byte size of a base64 data URL. */
export function dataUrlByteSize(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  const data = comma < 0 ? '' : dataUrl.slice(comma + 1);
  const padding = data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((data.length * 3) / 4) - padding);
}

/** Scale that fits `width` × `height` within {@link AI_IMAGE_MAX_EDGE} (never enlarges). */
export function downscaleFactor(width: number, height: number, maxEdge: number = AI_IMAGE_MAX_EDGE): number {
  const edge = Math.max(width, height);
  return edge > maxEdge ? maxEdge / edge : 1;
}

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('unreadable image'));
    };
    image.src = url;
  });

/**
 * The file as a data URL the server accepts: downscaled to
 * {@link AI_IMAGE_MAX_EDGE}, JPEG for photos, PNG otherwise (sketches keep
 * crisp lines). Null when it is not a readable image or still too large.
 */
export async function prepareReferenceImage(file: File): Promise<string | null> {
  if (!isAcceptedImageType(file.type)) return null;
  try {
    const image = await loadImage(file);
    const scale = downscaleFactor(image.naturalWidth, image.naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const dataUrl = file.type === 'image/jpeg' ? canvas.toDataURL('image/jpeg', 0.9) : canvas.toDataURL('image/png');
    return dataUrlByteSize(dataUrl) <= AI_MAX_IMAGE_BYTES ? dataUrl : null;
  } catch {
    return null;
  }
}

/** The first image file of a paste, if any. */
export function pastedImageFile(data: DataTransfer | null): File | null {
  if (!data) return null;
  for (const file of Array.from(data.files)) if (isAcceptedImageType(file.type)) return file;
  return null;
}
